import { KeyboardEvent, useRef } from 'react';

import styled from 'styled-components';

import { useValue } from '@components/component-helpers';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconSearch } from '@components/icons/search';
import { TextField, TextFieldProps } from '@components/text-field';
import { theme } from '@styles/theme';
import { transitionSlow } from '@utils/transitions';

type TextFieldBase = Omit<TextFieldProps, 'label' | 'hideLabel' | 'iconLeft' | 'iconRight'>;
export type SearchBoxProps = TextFieldBase & {
  shouldBlurOnEscape?: boolean;
};

export const SearchBox = ({
  onChange: userOnChange = () => null,
  onChangeDebounce,
  defaultValue = '',
  dataTestid,
  value: controlledValue,
  shouldBlurOnEscape = true,
  ...rest
}: SearchBoxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isControlled = controlledValue !== undefined;
  const { onChange, value } = useValue({
    defaultValue,
    onChange: userOnChange,
    // A controlled caller owns the value; debouncing it drops keystrokes. `TextField` does the same.
    onChangeDebounce: isControlled ? 0 : onChangeDebounce,
  });
  const finalValue = controlledValue ?? value;
  const hasValue = finalValue !== '';

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') return;
    if (hasValue) {
      event.stopPropagation();
      onChange('');
      // Pre-empt TextField's onBlur, which would otherwise trim-restore the stale DOM value.
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
    // Inside a menu or popover the panel handles the next Escape itself, and it
    // only sees it while focus is still within the panel.
    if (shouldBlurOnEscape) {
      inputRef.current?.blur();
    }
  };

  return (
    <SearchBoxContainer
      className="search-box-container"
      data-hasvalue={hasValue}
      data-testid={dataTestid}
    >
      <TextField
        ref={inputRef}
        label="Search"
        hideLabel
        onChange={onChange}
        value={finalValue}
        onKeyDown={handleKeyDown}
        iconLeft={
          <span className="search-box-left-icon">
            <IconSearch />
          </span>
        }
        iconRight={
          <IconCloseRegular
            className="text-clear-icon"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            style={{ opacity: hasValue ? 1 : 0, transition: transitionSlow('opacity') }}
          />
        }
        dataTestid="search-box-input-field"
        {...rest}
      />
    </SearchBoxContainer>
  );
};

const SearchBoxContainer = styled.div({
  '.search-box-left-icon': {
    display: 'flex',
  },

  '&[data-hasvalue="true"]': {
    '.search-box-left-icon': {
      '--icon-color': theme.color.elementBlue100,
    },
  },
});
